import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { datadogRum } from "@datadog/browser-rum";
import SectionTitle from "../SectionTitle/SectionTitle";
import InputShortText from "../InputShortText/InputShortText";
import TextArea from "../TextArea/TextArea";
import SalesAPI, { Sale, SaleInstalment } from "../../api/SalesAPI";
import SelectInput from "../SelectInput/SelectInput";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import InputNumber from "../InputNumber/InputNumber";
import PriceField from "../PriceField/PriceField";
import CONFIG from "../../config/config";
import { useSelector } from "react-redux";
import { isValidEmail, isValidPhone, isValidText } from "../../shared/validation";
import { useFormValidation } from "../../hooks/useFormValidation";
import EmployeesAPI, { Employee } from "../../api/EmployeesAPI";
import TextOptionalActionButton from "../TextOptionalActionButton/TextOptionalActionButton";
import { Plus, Calendar, Check, X, FileText, Trash2 } from "lucide-react";
import SmallCheckBox from "../SmallCheckBox/SmallCheckBox";
import IconButton from "../IconButton/IconButton";
import Tooltip from "../Tooltip/Tooltip";
import { toastService } from "../../services/ToastService";
import CustomerInput from "../CustomerInput/CustomerInput";
import { Customer } from "../../api/CustomersAPI";
import WarningNotice from "../WarningNotice/WarningNotice";
import { ItemAutocomplete } from "../ItemAutocomplete/ItemAutocomplete";
import { ItemVariant } from "../../api/CollectionItemVariantsAPI";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput";
import { useContractTemplate } from "../../hooks/useContractTemplate";
import { Modal } from "../Modal/Modal";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import CollectionsAPI, { Item, Collection } from "../../api/CollectionsAPI";
import Divider from "../Divider/Divider";
import Card from "../Card/Card";
import Subtitle from "../Subtitle/Subtitle";
import TinyText from "../TinyText/TinyText";
import { useStoreContractLocale } from "../../hooks/useStoreContractLocale";
import FilesAPI from "../../api/FilesAPI";
import { extractFileName } from "../PrivateResourceLink/PrivateResourceLink";
import { AdminRestricted } from "../AdminRestricted/AdminRestricted";
import { getDefaultCurrency } from "../../utils/currencyDetection";
import { SaleOpinionSection } from "../SaleOpinionSection/SaleOpinionSection";

type Props = {
  sale: Sale | null;
  onSaleChange?: (value: Sale) => void;
  isDisabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onNavigationChange?: (handlers: {
    currentStep: number;
    totalSteps: number;
    canProceed: () => boolean;
    handleNext: () => void;
    handleBack: () => void;
    buttonLabel?: string;
  }) => void;
};

const getInitialState = (sale: Sale | null) => {
  if (sale) {
    return {
      customerName: isValidText(sale.customerName || '', 2),
      dressName: isValidText(sale.dressName || '', 2),
    };
  }
};

const STEPS = [
  { id: 1, key: 'customer-info', titleKey: 'sales.form.tabs.customer' },
  { id: 2, key: 'product-data', titleKey: 'sales.form.tabs.product' },
  { id: 3, key: 'payments', titleKey: 'sales.form.tabs.payments' },
  { id: 4, key: 'summary', titleKey: 'sales.form.tabs.summary' },
  { id: 5, key: 'opinion-reminder', titleKey: 'sales.form.tabs.opinion_reminder' },
  { id: 6, key: 'contract', titleKey: 'sales.form.tabs.contract' },
];

function SaleFormSteps({ sale, onSaleChange = () => null, isDisabled = false, onValidationChange, onComplete, onCancel, onNavigationChange }: Props) {
  const { t } = useTranslation();
  const settings = useSelector((state: any) => state.settings.value);
  const requiredFields = ['customerName', 'dressName'];
  const initialState = getInitialState(sale);
  const { updateFieldValidation, isFormValid } = useFormValidation(requiredFields, initialState);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showVeilFields, setShowVeilFields] = useState(false);
  const [showDescriptionFields, setShowDescriptionFields] = useState(false);
  const [showMeasurementsFields, setShowMeasurementsFields] = useState(false);
  const [showSizeField, setShowSizeField] = useState(false);
  const [showAdditionalMeasurementsFields, setShowAdditionalMeasurementsFields] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [variants, setVariants] = useState<ItemVariant[]>([]);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null); // Store the current item ID for variant clearing
  const { contractTemplate, setLocale: setContractTemplateLocale } = useContractTemplate();
  const storeContractLocale = useStoreContractLocale();
  const [shouldAutoCreateInstalments, setShouldAutoCreateInstalments] = useState(false);
  const [, setGenerateContract] = useState(false);
  const [contractSigners, setContractSigners] = useState<Employee[]>([]);
  const [selectedContractLanguage, setSelectedContractLanguage] = useState<string>('');
  const [completionDate, setCompletionDate] = useState<Date | null>(null);
  // Contract-specific fields (not stored in Sale entity)
  const [contractClientPersonalID, setContractClientPersonalID] = useState<string>('');
  const [contractClientAddress, setContractClientAddress] = useState<string>('');
  const [contractDressTrain, ] = useState<string>('');
  const [contractDressLegCut, ] = useState<string>('');
  const [contractEmployeeName, setContractEmployeeName] = useState<string>('');
  const [contractPaymentSpecifications, ] = useState<string>('');
  const [firstFollowUpMeetingDate, setFirstFollowUpMeetingDate] = useState<Date | null>(null);
  const [showReceiptNumberField, setShowReceiptNumberField] = useState(false);
  // Local state to track which instalments have due date field visible
  const [visibleDueDateFields, setVisibleDueDateFields] = useState<Set<number>>(new Set());
  // Local state to track which instalments have invoice field visible
  const [visibleInvoiceFields, setVisibleInvoiceFields] = useState<Set<number>>(new Set());
  // State for "Add a new model" functionality
  const [showItemModal, setShowItemModal] = useState(false);
  const [newItem, setNewItem] = useState<Item | null>(null);
  const [showCatalogueSelectionModal, setShowCatalogueSelectionModal] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
  
  // State for "Add employee" functionality
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState<Employee | null>(null);
  
  // State for saving sale
  const [isSavingSale, setIsSavingSale] = useState(false);
  
  const [wantToGenerateContract, setWantToGenerateContract] = useState(false);
  

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid());
    }
  }, [isFormValid, onValidationChange]);

  const [localSale, setLocalSale] = useState<Sale | null>(sale);

  // Store's default currency — used only when adding a new item (Add Model), not for the sale form
  const storeDefaultCurrencyForNewItem = useMemo(() => {
    return getDefaultCurrency(undefined, settings?.currency, settings?.isLoggedIn);
  }, [settings?.currency, settings?.isLoggedIn]);

  const resolvedCurrency = useMemo(() => {
    return getDefaultCurrency(
      localSale?.currency,
      settings?.currency,
      settings?.isLoggedIn
    );
  }, [localSale?.currency, settings?.currency, settings?.isLoggedIn]);
  
  // Initialize visible fields based on existing values
  useEffect(() => {
    if (localSale?.instalments) {
      const dueDateFields = new Set<number>();
      const invoiceFields = new Set<number>();
      localSale.instalments.forEach((instalment, index) => {
        if (instalment.dueDate) {
          dueDateFields.add(index);
        }
        if (instalment.invoiceReference) {
          invoiceFields.add(index);
        }
      });
      setVisibleDueDateFields(dueDateFields);
      setVisibleInvoiceFields(invoiceFields);
    }
  }, [localSale?.instalments]);

  useEffect(() => {
    if (sale) {
      // Set status to JUST_SIGNED by default if not set
      const saleWithStatus = !sale.status ? { ...sale, status: 'JUST_SIGNED' as const } : sale;
      setLocalSale(saleWithStatus);
      if (!sale.status) {
        onSaleChange(saleWithStatus);
      }
    } else {
      setLocalSale(null);
    }
    setShowVeilFields(!!(sale?.dressVeil || sale?.dressVeilLength));
    setShowDescriptionFields(!!sale?.dressDescription);
    setShowMeasurementsFields(!!(sale?.bust || sale?.waist || sale?.hips));
    setShowSizeField(!!sale?.size);
    setShowAdditionalMeasurementsFields(!!sale?.measurements);
    // Set current item ID if productId exists and is an item (not variant)
    if (sale?.productId && sale?.productType === 'CATALOG_ITEM') {
      setCurrentItemId(sale.productId);
    }
    // Initialize generateContract from sale if it exists
    if (sale && 'generateContract' in sale) {
      setGenerateContract((sale as any).generateContract || false);
    }
    // Initialize receipt number field visibility
    setShowReceiptNumberField(!!sale?.receiptNumber);
    setFirstFollowUpMeetingDate(null);
  }, [sale, onSaleChange]);

  // Helper function to create instalments from template
  const createInstalmentsFromTemplate = useCallback((totalPrice: number, currency: string, templateInstalments?: Array<{ percentage: number; method: string }>) => {
    const instalmentsToUse = templateInstalments || contractTemplate?.payment?.instalments;
    if (!instalmentsToUse || instalmentsToUse.length === 0) {
      return [];
    }
    
    let calculatedTotal = 0;
    
    const newInstalments: SaleInstalment[] = instalmentsToUse.map((instalment, index) => {
      // Percentages are stored as whole numbers (e.g., 50 for 50%)
      const percentageValue = typeof instalment.percentage === 'number' ? instalment.percentage : parseFloat(String(instalment.percentage)) || 0;
      // Convert percentage to decimal (50 -> 0.5)
      const percentageMultiplier = percentageValue / 100;
      
      // Calculate amount based on percentage
      const amount = parseFloat((totalPrice * percentageMultiplier).toFixed(2));
      calculatedTotal += amount;
      
      console.log(`Instalment ${index + 1}: percentage=${percentageValue}, multiplier=${percentageMultiplier}, amount=${amount}, totalPrice=${totalPrice}`);
      
      // Map payment method (case-insensitive, handle both 'Card'/'CARD' and 'Transfer'/'TRANSFER')
      const methodValue = instalment.method || '';
      const methodLower = methodValue.toLowerCase();
      let paymentMethod: 'CARD' | 'TRANSFER' | 'CASH' = 'CASH';
      if (methodLower === 'card') {
        paymentMethod = 'CARD';
      } else if (methodLower === 'transfer') {
        paymentMethod = 'TRANSFER';
      } else if (methodLower === 'cash') {
        paymentMethod = 'CASH';
      }
      
      return {
        amount,
        currency: currency || CONFIG.DEFAULT_CURRENCY,
        paymentMethod,
      };
    });
    
    console.log('Before adjustment - calculatedTotal:', calculatedTotal, 'totalPrice:', totalPrice, 'difference:', totalPrice - calculatedTotal);
    
    // Adjust first instalment to account for rounding differences
    if (newInstalments.length > 0 && Math.abs(calculatedTotal - totalPrice) > 0.01) {
      const difference = parseFloat((totalPrice - calculatedTotal).toFixed(2));
      newInstalments[0].amount = parseFloat((newInstalments[0].amount + difference).toFixed(2));
      console.log('After adjustment - first instalment amount:', newInstalments[0].amount);
    }
    
    console.log('Final instalments:', newInstalments);
    
    return newInstalments;
  }, [contractTemplate?.payment?.instalments]);

  // Create instalments from contract template when totalPrice is set from item selection
  useEffect(() => {
    if (shouldAutoCreateInstalments && localSale?.totalPrice) {
      // Use template instalments if available, otherwise default to 2
      const instalmentCount = contractTemplate?.payment?.instalments?.length > 0 
        ? contractTemplate.payment.instalments.length 
        : 2;
      
      let newInstalments: SaleInstalment[];
      
      if (contractTemplate?.payment?.instalments?.length > 0) {
        // Use template instalments with percentages to calculate amounts
        newInstalments = createInstalmentsFromTemplate(
          localSale.totalPrice, 
          resolvedCurrency,
          contractTemplate.payment.instalments
        );
      } else {
        // Default to 2 instalments with TRANSFER method (split evenly)
        const amountPerInstalment = parseFloat((localSale.totalPrice / instalmentCount).toFixed(2));
        const remainder = parseFloat((localSale.totalPrice - (amountPerInstalment * instalmentCount)).toFixed(2));
        
        newInstalments = Array.from({ length: instalmentCount }, (_, index) => ({
          amount: index === 0 ? amountPerInstalment + remainder : amountPerInstalment,
          currency: resolvedCurrency,
          paymentMethod: 'TRANSFER' as const,
        }));
      }
      
      // Only update if instalments haven't been manually modified
      if (!localSale.instalments || localSale.instalments.length === 0) {
        handleChange({ instalments: newInstalments });
        setShouldAutoCreateInstalments(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoCreateInstalments, localSale?.totalPrice, contractTemplate?.payment?.instalments, resolvedCurrency, createInstalmentsFromTemplate]);

  // Load instalments from template when Payments step is active and instalments are empty
  useEffect(() => {
    if (currentStep === 3 && localSale?.totalPrice && (!localSale.instalments || localSale.instalments.length === 0) && contractTemplate?.payment?.instalments?.length > 0) {
      const newInstalments = createInstalmentsFromTemplate(
        localSale.totalPrice, 
        resolvedCurrency,
        contractTemplate.payment.instalments
      );
      if (newInstalments.length > 0) {
        handleChange({ instalments: newInstalments });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, localSale?.totalPrice, localSale?.instalments, contractTemplate?.payment?.instalments, resolvedCurrency, createInstalmentsFromTemplate]);

  useEffect(() => {
    const fetchContractSigners = async () => {
      try {
        const resp = await EmployeesAPI.getContractSigners();
        setContractSigners(resp.data);
      } catch (error) {
        console.error("Failed to fetch contract signers:", error);
      }
    };
    fetchContractSigners();
  }, []);

  // Load contract template for user's current language on mount
  useEffect(() => {
    if (!selectedContractLanguage) {
      setSelectedContractLanguage(storeContractLocale);
      setContractTemplateLocale(storeContractLocale);
    }
  }, [storeContractLocale, selectedContractLanguage, setContractTemplateLocale]);

  // Calculate sum of instalments for warning comparison
  const instalmentsSum = useMemo(() => {
    if (!localSale?.instalments || localSale.instalments.length === 0) {
      return 0;
    }
    return localSale.instalments.reduce((sum, instalment) => {
      return sum + (instalment.amount || 0);
    }, 0);
  }, [localSale?.instalments]);

  // Check if instalments sum matches total price (with small tolerance for floating point)
  const instalmentsMatchTotal = useMemo(() => {
    if (!localSale?.totalPrice || !localSale?.instalments || localSale.instalments.length === 0) {
      return true;
    }
    return Math.abs(instalmentsSum - localSale.totalPrice) < 0.01;
  }, [instalmentsSum, localSale?.totalPrice, localSale?.instalments]);

  useMemo(() => {
    const fetchEmployees = async () => {
      try {
        const response = await EmployeesAPI.getSalesRepresentatives();
        setEmployees(response.data);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchContractSigners = async () => {
      try {
        const resp = await EmployeesAPI.getContractSigners();
        setContractSigners(resp.data);
      } catch (error) {
        console.error("Failed to fetch contract signers:", error);
      }
    };
    fetchContractSigners();
  }, []);

  useEffect(() => {
    const fetchContractSigners = async () => {
      try {
        const resp = await EmployeesAPI.getContractSigners();
        setContractSigners(resp.data);
      } catch (error) {
        console.error("Failed to fetch contract signers:", error);
      }
    };
    fetchContractSigners();
  }, []);

  const handleChange = useCallback((updates: Partial<Sale>) => {
    if (localSale) {
      const updated = { ...localSale, ...updates };
      setLocalSale(updated);
      onSaleChange(updated);
    }
  }, [localSale, onSaleChange]);

  const handleInstalmentChange = (index: number, updates: Partial<SaleInstalment>) => {
    if (localSale?.instalments) {
      const updatedInstalments = [...localSale.instalments];
      updatedInstalments[index] = { ...updatedInstalments[index], ...updates };
      handleChange({ instalments: updatedInstalments });
    }
  };

  const handleAddInstalment = () => {
    if (!localSale) return;
    const newInstalment: SaleInstalment = {
      amount: 0,
      currency: resolvedCurrency,
      paymentMethod: 'TRANSFER',
    };
    const updatedInstalments = [...(localSale.instalments || []), newInstalment];
    handleChange({ instalments: updatedInstalments });
  };

  const handleRemoveInstalment = (index: number) => {
    if (localSale?.instalments) {
      const updatedInstalments = localSale.instalments.filter((_, i) => i !== index);
      handleChange({ instalments: updatedInstalments });
    }
  };

  const handleToggleDueDateField = (index: number) => {
    setVisibleDueDateFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleMarkAsPaid = (index: number) => {
    if (localSale?.instalments) {
      const updatedInstalments = [...localSale.instalments];
      updatedInstalments[index] = {
        ...updatedInstalments[index],
        isPaid: true,
        paidDate: new Date().toISOString().split('T')[0],
      };
      handleChange({ instalments: updatedInstalments });
      // Hide invoice field when marking as paid
      setVisibleInvoiceFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleMarkAsUnpaid = (index: number) => {
    if (localSale?.instalments) {
      const updatedInstalments = [...localSale.instalments];
      updatedInstalments[index] = {
        ...updatedInstalments[index],
        isPaid: false,
        paidDate: undefined,
        invoiceReference: undefined,
      };
      handleChange({ instalments: updatedInstalments });
      // Hide invoice field when marking as unpaid
      setVisibleInvoiceFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleToggleInvoiceField = (index: number) => {
    setVisibleInvoiceFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleCreateItem = () => {
    setNewItem({
      name: '',
      featuredPictureUrl: '',
      type: 'dresses',
      placeOrder: 0,
      price: 0,
      currency: storeDefaultCurrencyForNewItem,
      description: '',
      media: [],
      variations: []
    });
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!newItem || !newItem.name) {
      toastService.showToast(t("errors.too_short") || "Item name is required", 'error');
      return;
    }

    if (!newItem.price || newItem.price <= 0) {
      toastService.showToast(t("sales.form.item.price_required") || "Price is required", 'error');
      return;
    }

    try {
      // Fetch collections to see if any exist
      const collectionsResponse = await CollectionsAPI.getCollections();
      const availableCollections = collectionsResponse.data || [];

      setCollections(availableCollections);

      if (availableCollections.length === 0) {
        // No collections exist, create a default one and add the item
        const defaultCollection: Collection = {
          name: 'Default Collection',
          shortName: 'default',
          description: 'Default collection for items',
          type: 'dresses',
          types: ['dresses'],
          views: 0,
          featured: false,
          public: false,
          lastUpdateTime: null,
          items: [],
          shareableByLink: false,
          pricesArePublic: false
        };
        
        const createdCollection = await CollectionsAPI.add(defaultCollection);
        const collectionId = createdCollection.data.id!;
        
        // Add item to the default collection
        const createdItem = await CollectionsAPI.addItem(collectionId, newItem);
        
        toastService.showToast(
          t("sales.form.item.created_with_default_collection") || 
          "Item created and added to catalog. You can configure the catalog in the Catalogues section.",
          "success"
        );
        
        // Get the created item with its ID from the response
        const itemWithId = createdItem.data || { ...newItem };
        
        // Trigger the item selection handler to simulate selecting the item
        handleItemSelection(itemWithId);
        
        setShowItemModal(false);
        setNewItem(null);
      } else if (availableCollections.length === 1) {
        // Only one collection exists, add item directly
        const collectionId = availableCollections[0].id!;
        const createdItem = await CollectionsAPI.addItem(collectionId, newItem);
        
        toastService.showToast(t("sales.form.item.created") || "Item created successfully", "success");
        
        // Get the created item with its ID from the response
        const itemWithId = createdItem.data || { ...newItem };
        
        // Trigger the item selection handler to simulate selecting the item
        handleItemSelection(itemWithId);
        
        setShowItemModal(false);
        setNewItem(null);
      } else {
        // Multiple collections exist, show selection modal
        setShowItemModal(false);
        setShowCatalogueSelectionModal(true);
      }
    } catch (error) {
      toastService.showToast(t("sales.form.item.create_error") || "Failed to create item", 'error');
    }
  };

  const handleAddItemToCollection = async () => {
    if (!selectedCollectionId || !newItem) {
      toastService.showToast(t("sales.form.item.select_collection") || "Please select a collection", 'error');
      return;
    }

    try {
      const createdItem = await CollectionsAPI.addItem(selectedCollectionId, newItem);
      toastService.showToast(t("sales.form.item.created") || "Item created successfully", "success");
      
      // Get the created item with its ID from the response
      const itemWithId = createdItem.data || { ...newItem };
      
      // Trigger the item selection handler to simulate selecting the item
      handleItemSelection(itemWithId);
      
      setShowCatalogueSelectionModal(false);
      setNewItem(null);
      setSelectedCollectionId(null);
    } catch (error) {
      toastService.showToast(t("sales.form.item.create_error") || "Failed to create item", 'error');
    }
  };

  const contains = (employee: Employee, flag: string) => {
    return (employee?.flags || []).some(f => f.toLowerCase() === flag.toLowerCase());
  };

  const handleCreateEmployee = () => {
    setNewEmployee({
      name: '',
      active: true,
      flags: ['contractSignature', 'salesRepresentative'], // Pre-select signing contract (required) and sales representative
      note: ''
    });
    setShowEmployeeModal(true);
  };

  const handleSaveEmployee = async () => {
    if (!newEmployee || !newEmployee.name) {
      toastService.showToast(t("errors.too_short") || "Employee name is required", 'error');
      return;
    }

    try {
      await EmployeesAPI.add(newEmployee);
      toastService.showToast(t("sales.form.employee.created") || "Employee created successfully", "success");
      setShowEmployeeModal(false);
      const employeeName = newEmployee.name;
      setNewEmployee(null);
      // Reload employees
      const employeesResponse = await EmployeesAPI.getSalesRepresentatives();
      setEmployees(employeesResponse.data);
      // Auto-select the newly created employee by name
      const createdEmployee = employeesResponse.data.find(emp => emp.name === employeeName);
      if (createdEmployee?.id && localSale) {
        handleChange({ employeeId: String(createdEmployee.id) });
      }
    } catch (error) {
      toastService.showToast(t("sales.form.employee.create_error") || "Failed to create employee", 'error');
    }
  };

  const handleViewContractPDF = async () => {
    if (!localSale?.attachmentUrl) {
      toastService.showToast(
        t("sales.actions.view_contract_pdf.error") || "Contract PDF URL not available",
        "error"
      );
      return;
    }

    try {
      const fileName = extractFileName(localSale.attachmentUrl);
      if (!fileName) {
        toastService.showToast(
          t("sales.actions.view_contract_pdf.error") || "Could not extract filename from contract URL",
          "error"
        );
        return;
      }

      const urlResponse = await FilesAPI.getUrl(fileName, 'contracts');
      const signedUrl = urlResponse.data.url;
      
      window.open(signedUrl, "_blank")?.focus();
      
      toastService.showToast(
        t("sales.actions.view_contract_pdf.success") || `Opening contract PDF for sale ${localSale.receiptNumber || localSale.id}...`,
        "success"
      );
    } catch (error) {
      console.error("Failed to open contract PDF:", error);
      toastService.showToast(
        t("sales.actions.view_contract_pdf.error") || "Failed to open contract PDF",
        "error"
      );
    }
  };

  const handleGenerateContractPDF = useCallback(async () => {
    if (!localSale?.id) {
      toastService.showToast(
        t("sales.actions.generate_contract.error.no_sale") || "Sale must be saved first",
        "error"
      );
      return;
    }

    try {
      const request: {
        completionDate?: string;
        clientPersonalID?: string;
        clientAddress?: string;
        dressTrain?: string;
        dressLegCut?: string;
        totalPriceWording?: string;
        contractEmployeeName?: string;
        contractLanguage?: string;
        paymentSpecifications?: string;
        firstFollowUpMeetingDate?: string;
      } = {};
      
      if (completionDate) {
        request.completionDate = completionDate.toISOString().split('T')[0];
      }
      if (contractClientPersonalID) {
        request.clientPersonalID = contractClientPersonalID;
      }
      if (contractClientAddress) {
        request.clientAddress = contractClientAddress;
      }
      if (contractDressTrain) {
        request.dressTrain = contractDressTrain;
      }
      if (contractDressLegCut) {
        request.dressLegCut = contractDressLegCut;
      }
      if (localSale?.totalPriceInWords) {
        request.totalPriceWording = localSale.totalPriceInWords;
      }
      if (contractEmployeeName) {
        request.contractEmployeeName = contractEmployeeName;
      }
      if (selectedContractLanguage) {
        request.contractLanguage = selectedContractLanguage;
      }
      if (contractPaymentSpecifications) {
        request.paymentSpecifications = contractPaymentSpecifications;
      }
      if (firstFollowUpMeetingDate) {
        request.firstFollowUpMeetingDate = firstFollowUpMeetingDate.toISOString();
      }
      
      const response = await SalesAPI.generateContractPDF(localSale.id, Object.keys(request).length > 0 ? request : undefined);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      
      // Refresh sale to get updated attachmentUrl
      try {
        const updatedSale = await SalesAPI.getById(localSale.id);
        handleChange(updatedSale.data);
      } catch (refreshError) {
        // Ignore refresh errors, PDF was still generated
        console.warn("Failed to refresh sale after PDF generation:", refreshError);
      }
      
      toastService.showToast(
        t("sales.actions.generate_contract.success") || "Contract PDF generated successfully",
        "success"
      );
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        t("sales.actions.generate_contract.error") || "Failed to generate contract PDF";
      toastService.showToast(errorMessage, 'error');
    }
  }, [localSale, t, completionDate, contractClientPersonalID, contractClientAddress, contractDressTrain, contractDressLegCut, contractEmployeeName, selectedContractLanguage, contractPaymentSpecifications, firstFollowUpMeetingDate, handleChange]);

  const handleItemSelection = useCallback((item: Item) => {
    setVariants(item.variations || []);
    setCurrentItemId(item.id?.toString() || null);
    const updates: Partial<Sale> = { 
      dressName: item.name,
      productId: item.id?.toString(),
      productType: 'CATALOG_ITEM' as const,
      productVariant: undefined // Clear variant when item changes
    };
    
    // Extract price and currency from item if available
    if (item.price !== undefined) {
      updates.totalPrice = item.price;
      setShouldAutoCreateInstalments(true);
    }
    if (item.currency) {
      updates.currency = item.currency;
    }
    
    handleChange(updates);
    updateFieldValidation('dressName', isValidText(item.name, 2));
  }, [handleChange, updateFieldValidation]);

  const paymentMethods = [
    { value: 'CARD', readableValue: t('payment.methods.card') || 'Card' },
    { value: 'TRANSFER', readableValue: t('payment.methods.transfer') || 'Transfer' },
    { value: 'CASH', readableValue: t('payment.methods.cash') || 'Cash' }
  ];

  const fillCustomerData = (customer: Customer) => {
    handleChange({
      customerName: customer.name,
      customerSurname: customer.surname,
      customerPhone: customer.phone || '',
      customerEmail: customer.email || '',
      customer: customer,
      customerId: customer.id ? Number(customer.id) : undefined,
      clientName: `${customer.name}${customer.surname ? ` ${customer.surname}` : ''}`,
    });
    updateFieldValidation('customerName', isValidText(customer.name, 2));
  };

  const handleCustomerNameValidation = useCallback((isValid: boolean) => {
    updateFieldValidation('customerName', isValid);
  }, [updateFieldValidation]);

  const canProceed = useMemo(() => {
    return (): boolean => {
      if (!localSale) return false;
      
      switch (currentStep) {
        case 1:
          // Check if customer name is valid (either from customer object or direct field)
          const customerName = localSale.customer?.name || localSale.customerName || '';
          return isValidText(customerName, 2);
        case 2:
          return isValidText(localSale.dressName || '', 2);
        case 3:
          return true; // Payments step is always valid
        case 4:
          return !isSavingSale && !localSale?.id; // Can proceed if not saving and sale not yet saved
        case 5:
          return !!localSale?.id; // Can proceed if sale is saved
        case 6:
          return !!localSale?.id; // Can proceed if sale is saved
        default:
          return false;
      }
    };
  }, [localSale, currentStep, isSavingSale]);

  const handleRegisterSale = useCallback(async () => {
    if (!localSale || isSavingSale) return;
    
    setIsSavingSale(true);
    
    try {
      // Ensure clientName is set from customer name/surname
      const saleToSave: Sale = {
        ...localSale,
        clientName: localSale.clientName || `${localSale.customerName || ''} ${localSale.customerSurname || ''}`.trim() || '',
        productId: localSale.productId,
        productType: localSale.productType,
      };

      const response = await SalesAPI.add(saleToSave);
      const savedSale = response.data;
      
      datadogRum.addAction("Register Sale", {
        productName: saleToSave.dressName || "",
        totalPrice: saleToSave.totalPrice || 0,
        currency: saleToSave.currency || "",
        source: "calendar",
      });
      
      // Update local sale with saved data (including ID)
      handleChange(savedSale);
      
      toastService.showToast(
        t("sales.create.success") || "Sale registered successfully",
        "success"
      );
      
      // Move to next step (opinion reminder)
      setCurrentStep(5);
    } catch (error: any) {
      console.error('Error creating sale:', error);
      const errorMessage = error?.response?.data?.message || 
        t("sales.save.error") || "Failed to save sale";
      toastService.showToast(errorMessage, 'error');
    } finally {
      setIsSavingSale(false);
    }
  }, [localSale, isSavingSale, handleChange, t]);

  const handleNext = useCallback(async () => {
    if (!canProceed()) return;
    
    // Step 4: Register sale, then auto-advance to step 5
    if (currentStep === 4 && !localSale?.id) {
      await handleRegisterSale();
      return; // handleRegisterSale will advance to step 5
    }
    
    // Step 5: Opinion reminder; scheduling handled inside SaleOpinionSection, just advance
    if (currentStep === 5) {
      setCurrentStep(6);
      return;
    }
    
    // Step 6: Handle contract PDF generation if wanted, then complete
    if (currentStep === 6) {
      if (wantToGenerateContract && localSale?.id) {
        try {
          await handleGenerateContractPDF();
        } catch (error) {
          console.error("Failed to generate contract PDF:", error);
          // Don't block completion if PDF generation fails
        }
      }
      // Complete the flow
      if (onComplete) {
        onComplete();
      }
      return;
    }
    
    // Steps 1-3: Normal navigation
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else if (onComplete) {
      onComplete();
    }
  }, [canProceed, currentStep, localSale, handleRegisterSale, wantToGenerateContract, handleGenerateContractPDF, onComplete]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Expose navigation handlers to parent
  useEffect(() => {
    if (onNavigationChange) {
      // Determine button label based on current step
      let buttonLabel: string | undefined;
      if (currentStep === 4 && !localSale?.id) {
        buttonLabel = t("sales.form.register_sale") || "Register Sale";
      } else if (currentStep === 5) {
        buttonLabel = t("sales.form.continue") || "Continue";
      } else if (currentStep === 6) {
        // Step 6: Show "Generate Contract PDF" if user wants to generate, otherwise "Complete"
        buttonLabel = wantToGenerateContract 
          ? (t("sales.actions.generate_contract") || "Generate Contract PDF")
          : (t("sales.form.complete") || "Complete");
      } else if (currentStep < STEPS.length) {
        buttonLabel = t("sales.form.next") || "Next";
      } else {
        buttonLabel = t("sales.form.complete") || "Complete";
      }
      
      onNavigationChange({
        currentStep,
        totalSteps: STEPS.length,
        canProceed,
        handleNext,
        handleBack,
        buttonLabel
      });
    }
  }, [currentStep, localSale, wantToGenerateContract, onNavigationChange, handleNext, handleBack, canProceed, t]);

  if (!localSale) {
    return null;
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Customer Info
        return (
          <div className="space-y-5">
            {localSale?.hasContractPDF && (
              <div className="flex justify-end mb-4">
                <Tooltip content={t("sales.actions.view_contract_pdf") || "View Contract PDF"} position="bottom">
                  <IconButton
                    actionName="View Contract PDF"
                    onClick={handleViewContractPDF}
                  >
                    <FileText className="w-5 h-5 text-purple-600" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
            <div className="flex flex-wrap space-x-2">
              <div className="flex-1">
                <CustomerInput
                  onCustomerSelect={c => fillCustomerData(c)}
                  onValidationChange={handleCustomerNameValidation}
                  label={"sales.form.customer.name"}
                  onChange={e => handleChange({ customerName: e.currentTarget.value })}
                  errorMessage={"errors.too_short"}
                  defaultValue={localSale.customer?.name || localSale.customerName || ''}
                  initialCustomerValue={localSale.customer || undefined}
                  isDisabled={isDisabled}
                />
              </div>
              {!localSale.customer && (
                <InputShortText
                  label={t("sales.form.customer.surname") || "Surname"}
                  defaultValue={localSale.customerSurname || ''}
                  isDisabled={isDisabled}
                  onChange={(e) => handleChange({ customerSurname: e.target.value })}
                />
              )}
            </div>
            <br/>
            <PhoneNumberInput
              label={t("sales.form.customer.phone") || "Phone"}
              defaultValue={localSale.customerPhone || ''}
              isDisabled={isDisabled}
              onChange={(value: string) => handleChange({ customerPhone: value })}
              validationFn={(value: string) => !value || isValidPhone(value)}
            />
            <InputShortText
              label={t("sales.form.customer.email") || "Email"}
              defaultValue={localSale.customerEmail || ''}
              isDisabled={isDisabled}
              onChange={(e) => handleChange({ customerEmail: e.target.value })}
              validationFn={(value) => !value || isValidEmail(value)}
              errorMessage={"errors.invalid_email"}
            />
            <div className="flex flex-wrap space-x-5">
              <div className="flex-1">
                <CustomDatePicker
                  label={t("contracts.wedding_date") || "Wedding Date"}
                  value={localSale.weddingDate ? new Date(localSale.weddingDate) : undefined}
                  onChange={(date) => handleChange({ weddingDate: date?.toISOString() })}
                  isDisabled={isDisabled}
                  timeZone={settings.timezone}
                />
              </div>
              <div className="flex-1">
                <CustomDatePicker
                  label={t("sales.form.delivery_date") || "Delivery date (ready to pick up)"}
                  value={localSale.deliveryDate ? new Date(localSale.deliveryDate) : undefined}
                  onChange={(date) => handleChange({ deliveryDate: date?.toISOString() })}
                  isDisabled={isDisabled}
                  timeZone={settings.timezone}
                />
              </div>
            </div>
            {!showReceiptNumberField && !localSale?.receiptNumber ? (
              <div className="mt-2">
                <TextOptionalActionButton
                  text={t("sales.form.add_receipt_number") || "Add Receipt Number"}
                  onClick={() => setShowReceiptNumberField(true)}
                  disabled={isDisabled}
                  actionName="Add Receipt Number"
                />
              </div>
            ) : (
              <div className="mt-2">
                <InputShortText
                  label={t("sales.details.receipt_number") || "Receipt Number"}
                  defaultValue={localSale.receiptNumber || ''}
                  isDisabled={isDisabled}
                  onChange={(e) => {
                    handleChange({ receiptNumber: e.target.value });
                    // Hide field if receipt number is cleared
                    if (!e.target.value) {
                      setShowReceiptNumberField(false);
                    }
                  }}
                />
              </div>
            )}
            <div className="flex flex-wrap space-x-5">
              <div className="flex-1">
                <SelectInput
                  values={employees.map(e => { return { value: String(e.id!), readableValue: e.name }})}
                  onChange={(e) => handleChange({ employeeId: e.target.value })}
                  label={t("sales.form.employee_assigned") || "Sales Agent"}
                  defaultValue={localSale.employeeId}
                  isDisabled={isDisabled}
                />
                <AdminRestricted>
                  <div className="mt-2">
                    <TextOptionalActionButton
                      text={t("sales.form.create_employee") || "Create employee"}
                      onClick={handleCreateEmployee}
                      disabled={isDisabled}
                      actionName="Create Employee"
                    />
                  </div>
                </AdminRestricted>
              </div>
            </div>
          </div>
        );

      case 2: // Product Data
        return (
          <div className="space-y-5">
            <div className="space-y-4">
              <ItemAutocomplete
                onChange={handleItemSelection}
                label={t("sales.form.product.model") || "Model"}
                placeholder={"contracts.model.search.placeholder"}
                defaultValue={localSale.dressName || ''}
                isDisabled={isDisabled}
              />
              <AdminRestricted>
                <div className="mt-2">
                  <TextOptionalActionButton
                    text={t("sales.form.add_model") || "Add a new model"}
                    onClick={handleCreateItem}
                    disabled={isDisabled}
                    actionName="Add Model"
                  />
                </div>
              </AdminRestricted>
              {/* Only show other fields if a model is selected */}
              {localSale.dressName && (
                <>
                  {variants && variants.length > 0 && (
                <SelectInput
                  values={[
                    { value: '', readableValue: t("sales.form.product.no_variant") || "No variant" },
                    ...variants.map(v => ({ value: v.name, readableValue: v.name }))
                  ]}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (!selectedValue || selectedValue === '') {
                      // Variant cleared - reset to item
                      if (currentItemId) {
                        handleChange({ 
                          productVariant: undefined,
                          productId: currentItemId,
                          productType: 'CATALOG_ITEM' as const
                        });
                      } else {
                        // Fallback: try to use productId if it's an item (not variant)
                        const fallbackItemId = localSale.productId && localSale.productType === 'CATALOG_ITEM' 
                          ? localSale.productId 
                          : null;
                        if (fallbackItemId) {
                          handleChange({ 
                            productVariant: undefined,
                            productId: fallbackItemId,
                            productType: 'CATALOG_ITEM' as const
                          });
                        } else {
                          // Last resort: just clear variant
                          handleChange({ 
                            productVariant: undefined,
                            productType: 'CATALOG_ITEM' as const
                          });
                        }
                      }
                    } else {
                      const selectedVariant = variants.find(v => v.name === selectedValue);
                      handleChange({ 
                        productVariant: selectedValue,
                        productId: selectedVariant?.id?.toString(),
                        productType: 'CATALOG_ITEM_VARIANT' as const
                      });
                    }
                  }}
                  label={t("sales.form.product.variant") || "Variant"}
                  defaultValue={localSale.productVariant || ''}
                  isDisabled={isDisabled}
                  />
              )}
              {!showDescriptionFields && (
                <div className="mt-2">
                  <TextOptionalActionButton
                    text={t("sales.form.add_notes") || "Add notes"}
                    onClick={() => setShowDescriptionFields(true)}
                    disabled={isDisabled}
                    actionName="Add Notes"
                  />
                </div>
              )}
              {showDescriptionFields && (
                <TextArea
                  label={t("sales.form.product.description") || "Description"}
                  defaultValue={localSale.dressDescription || ''}
                  isDisabled={isDisabled}
                  onChange={(e) => handleChange({ dressDescription: e.target.value })}
                  maxLength={2000}
                />
              )}
              {!showVeilFields && (
                <div className="mt-2">
                  <TextOptionalActionButton
                    text={t("sales.form.add_veil") || "Add veil"}
                    onClick={() => setShowVeilFields(true)}
                    disabled={isDisabled}
                    actionName="Add Veil"
                  />
                </div>
              )}
              {showVeilFields && (
                <span className="flex space-x-5">
                  <SelectInput 
                    values={[
                      { value: '', readableValue: ''},
                      { value: 'Gładki', readableValue: 'contracts.veil.smooth'},
                      { value: 'Koronka', readableValue: 'contracts.veil.lace'}
                    ]}
                    onChange={(e) => handleChange({ dressVeil: e.target.value })}
                    label={"contracts.veil"}
                    defaultValue={localSale.dressVeil || ''}
                    isDisabled={isDisabled}
                  />
                  {localSale.dressVeil && (
                    <InputNumber 
                      label={"contracts.length"} 
                      validationFn={(value) => value >= 0} 
                      onChange={(e) => handleChange({ dressVeilLength: Number(e.target.value) || 0 })}
                      defaultValue={localSale.dressVeilLength || 0}
                      errorMessage={"errors.minimum_zero"}
                      isDisabled={isDisabled}
                    />
                  )}
                </span>
              )}
              <div className="mt-4">
                <SectionTitle text={t("sales.form.measurements") || "Measurements and Specifications"} />
                {!showSizeField && (
                  <div className="mt-2">
                    <TextOptionalActionButton
                      text={t("sales.form.add_size") || "Add size"}
                      onClick={() => setShowSizeField(true)}
                      disabled={isDisabled}
                      actionName="Add Size"
                    />
                  </div>
                )}
                {showSizeField && (
                  <div className="mt-2">
                    <InputShortText
                      label={t("sales.form.size") || "Size"}
                      defaultValue={localSale.size || ''}
                      isDisabled={isDisabled}
                      onChange={(e) => handleChange({ size: e.target.value })}
                      maxLength={20}
                    />
                  </div>
                )}
                {!showMeasurementsFields && (
                  <div className="mt-4">
                    <TextOptionalActionButton
                      text={t("sales.form.add_measurements") || "Add measurements"}
                      onClick={() => setShowMeasurementsFields(true)}
                      disabled={isDisabled}
                      actionName="Add Measurements"
                    />
                  </div>
                )}
                {showMeasurementsFields && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    <InputShortText
                      label={t("contracts.bust") || "Bust"}
                      defaultValue={localSale.bust || ''}
                      isDisabled={isDisabled}
                      onChange={(e) => handleChange({ bust: e.target.value })}
                    />
                    <InputShortText
                      label={t("contracts.waist") || "Waist"}
                      defaultValue={localSale.waist || ''}
                      isDisabled={isDisabled}
                      onChange={(e) => handleChange({ waist: e.target.value })}
                    />
                    <InputShortText
                      label={t("contracts.hip") || "Hips"}
                      defaultValue={localSale.hips || ''}
                      isDisabled={isDisabled}
                      onChange={(e) => handleChange({ hips: e.target.value })}
                    />
                  </div>
                )}
                {!showAdditionalMeasurementsFields && (
                  <div className="mt-4">
                    <TextOptionalActionButton
                      text={t("sales.form.add_additional_measurements") || "Add additional measurements"}
                      onClick={() => setShowAdditionalMeasurementsFields(true)}
                      disabled={isDisabled}
                      actionName="Add Additional Measurements"
                    />
                  </div>
                )}
                {showAdditionalMeasurementsFields && (
                  <div className="mt-4">
                    <TextArea
                      label={t("sales.form.measurements.additional") || "Additional Measurements"}
                      defaultValue={localSale.measurements || ''}
                      isDisabled={isDisabled}
                      onChange={(e) => handleChange({ measurements: e.target.value })}
                      maxLength={500}
                    />
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        );

      case 3: // Payments
        return (
          <div className="space-y-5">
            <div className="flex items-center justify-end">
              {!isDisabled && (
                <IconButton
                  actionName="Add Instalment"
                  onClick={handleAddInstalment}
                >
                  <Plus className="w-5 h-5 text-blue-600" />
                </IconButton>
              )}
            </div>
            <div className="space-y-4">
              {(localSale.instalments || []).map((instalment: SaleInstalment, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">
                      {t("sales.form.instalment") || "Instalment"} {index + 1}
                    </h4>
                    <div className="flex items-center space-x-2">
                      {!instalment.isPaid && !isDisabled && (
                        <button
                          onClick={() => handleMarkAsPaid(index)}
                          className="flex items-center space-x-1 px-2 py-1 rounded-full hover:bg-green-200 transition-colors group"
                          title={t('payment_plan.mark_as_paid') || 'Mark as paid'}
                        >
                          <Check className="h-4 w-4 text-green-600 group-hover:text-green-700" />
                          <span className="hidden sm:inline text-xs text-green-600 group-hover:text-green-700">
                            {t('payment_plan.mark_as_paid')}
                          </span>
                        </button>
                      )}
                      {instalment.isPaid && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            {t('payment_plan.paid') || 'Paid'}
                          </span>
                          {!isDisabled && (
                            <button
                              onClick={() => handleMarkAsUnpaid(index)}
                              className="p-1 rounded-full hover:bg-red-200 transition-colors group"
                              title={t('payment_plan.mark_as_unpaid') || 'Mark as unpaid'}
                            >
                              <X className="h-4 w-4 text-red-600 group-hover:text-red-700" />
                            </button>
                          )}
                        </div>
                      )}
                      {!isDisabled && (
                        <IconButton
                          actionName="Remove Instalment"
                          onClick={() => handleRemoveInstalment(index)}
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </IconButton>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex space-x-3">
                      <div className="flex-1">
                        <InputNumber
                          label={t("sales.form.instalment.amount") || "Amount"}
                          onChange={(e) => handleInstalmentChange(index, { amount: Number(e.target.value) || 0 })}
                          defaultValue={instalment.amount}
                          isDisabled={isDisabled}
                          validationFn={(value) => value >= 0}
                          errorMessage="errors.minimum_zero"
                        />
                      </div>
                      <div className="flex-1">
                        <SelectInput
                          label={t("sales.form.instalment.method") || "Payment Method"}
                          values={paymentMethods}
                          onChange={(e) => handleInstalmentChange(index, { paymentMethod: e.target.value as 'TRANSFER' | 'CARD' | 'CASH' })}
                          defaultValue={instalment.paymentMethod || 'TRANSFER'}
                          isDisabled={isDisabled}
                        />
                      </div>
                    </div>

                    <InputShortText
                      label={t("sales.form.instalment.amount_in_words") || "Amount in Words"}
                      defaultValue={instalment.amountInWords || ''}
                      onChange={(e) => handleInstalmentChange(index, { amountInWords: e.target.value })}
                      isDisabled={isDisabled}
                    />

                    {!visibleDueDateFields.has(index) && !instalment.dueDate ? (
                      <TextOptionalActionButton
                        text={t("payment_plan.assign_due_date") || "Assign a due date"}
                        onClick={() => handleToggleDueDateField(index)}
                        disabled={isDisabled}
                        actionName="Assign Due Date"
                        icon={<Calendar className="h-4 w-4" />}
                        variant="blue"
                      />
                    ) : (
                      <div>
                        <CustomDatePicker
                          label={t("payment_plan.due_date") || "Due Date"}
                          value={instalment.dueDate ? new Date(instalment.dueDate) : null}
                          onChange={(date) => {
                            handleInstalmentChange(index, { dueDate: date ? date.toISOString().split('T')[0] : undefined });
                            // Hide field if date is cleared
                            if (!date) {
                              handleToggleDueDateField(index);
                            }
                          }}
                          isDisabled={isDisabled}
                          timeZone={settings.timezone}
                        />
                      </div>
                    )}

                    {instalment.isPaid && (
                      <div>
                        <CustomDatePicker
                          label={t("payment_plan.paid_on") || "Paid on"}
                          value={instalment.paidDate ? new Date(instalment.paidDate) : new Date()}
                          onChange={(date) => handleInstalmentChange(index, { paidDate: date?.toISOString().split('T')[0] })}
                          isDisabled={isDisabled}
                          timeZone={settings.timezone}
                        />
                      </div>
                    )}

                    {instalment.isPaid && (
                      <div>
                        {!visibleInvoiceFields.has(index) && !instalment.invoiceReference ? (
                          <TextOptionalActionButton
                            text={t("payment_plan.add_invoice_reference") || "Add receipt reference"}
                            onClick={() => handleToggleInvoiceField(index)}
                            disabled={isDisabled}
                            actionName="Add Receipt Reference"
                            icon={null}
                            variant="blue"
                          />
                        ) : (
                          <div>
                            <InputShortText
                              label={t("payment_plan.invoice_reference") || "Receipt Reference"}
                              onChange={(e) => handleInstalmentChange(index, { invoiceReference: e.target.value })}
                              defaultValue={instalment.invoiceReference || ''}
                              isDisabled={isDisabled}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(!localSale.instalments || localSale.instalments.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  {t("sales.form.no_instalments") || "No instalments added. Click the + button to add one."}
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <PriceField 
                label={t("sales.form.total_price") || "Total Price"}
                currency={localSale.currency}
                price={localSale.totalPrice}
                currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                onCurrencyChange={(currency) => handleChange({ currency })}
                onPriceChange={(totalPrice) => handleChange({ totalPrice })}
                isDisabled={isDisabled}
              />
            <div className="mt-4">
              <InputShortText
                label={t("sales.form.contract.in_words") || t("contracts.in_words") || "Total In Words"}
                defaultValue={localSale.totalPriceInWords || ''}
                onChange={e => handleChange({ totalPriceInWords: e.target.value })}
                isDisabled={isDisabled}
              />
            </div>
              {localSale.instalments && localSale.instalments.length > 0 && !instalmentsMatchTotal && (
                <div className="mt-3">
                  <WarningNotice 
                    message={t("sales.form.instalments_mismatch_warning", {
                      instalmentsSum: instalmentsSum.toLocaleString(),
                      totalPrice: localSale.totalPrice.toLocaleString(),
                      currency: resolvedCurrency
                    }) || `Instalments total (${instalmentsSum.toLocaleString()} ${resolvedCurrency}) does not match total price (${localSale.totalPrice.toLocaleString()} ${resolvedCurrency})`}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 4: // Summary and Register Sale
        return (
          <div className="space-y-5">
            <Card>
              <>
                <Subtitle text={t("sales.form.summary.title") || "Sale Summary"} />
                <div className="space-y-4 mt-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t("sales.form.summary.customer") || "Customer"}:</p>
                    <p className="text-sm text-gray-900">{localSale.clientName || `${localSale.customerName || ''} ${localSale.customerSurname || ''}`.trim() || '-'}</p>
                    {localSale.customerPhone && (
                      <p className="text-sm text-gray-600">{localSale.customerPhone}</p>
                    )}
                    {localSale.customerEmail && (
                      <p className="text-sm text-gray-600">{localSale.customerEmail}</p>
                    )}
                  </div>
                  
                  <Divider />
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t("sales.form.summary.product") || "Product"}:</p>
                    <p className="text-sm text-gray-900">{localSale.dressName || '-'}</p>
                    {localSale.productVariant && (
                      <p className="text-sm text-gray-600">{t("sales.form.product.variant") || "Variant"}: {localSale.productVariant}</p>
                    )}
                  </div>
                  
                  <Divider />
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t("sales.form.summary.payment") || "Payment"}:</p>
                    <p className="text-sm text-gray-900">
                      {localSale.totalPrice?.toLocaleString()} {resolvedCurrency}
                    </p>
                    {localSale.instalments && localSale.instalments.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-600">{t("sales.form.summary.instalments") || "Instalments"}:</p>
                        {localSale.instalments.map((instalment, index) => (
                          <p key={index} className="text-xs text-gray-600 ml-2">
                            {index + 1}. {instalment.amount.toLocaleString()} {instalment.currency || resolvedCurrency} 
                            {' '}({t(`payment.methods.${instalment.paymentMethod?.toLowerCase()}`) || instalment.paymentMethod || 'N/A'})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {localSale.weddingDate && (
                    <>
                      <Divider />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{t("contracts.wedding_date") || "Wedding Date"}:</p>
                        <p className="text-sm text-gray-900">{new Date(localSale.weddingDate).toLocaleDateString()}</p>
                      </div>
                    </>
                  )}
                </div>
                
                {isSavingSale && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">{t("sales.form.saving") || "Saving sale..."}</p>
                  </div>
                )}
                
                {localSale?.id && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 text-center">
                      {t("sales.form.registered_success") || "✓ Sale registered successfully!"}
                    </p>
                  </div>
                )}
              </>
            </Card>
          </div>
        );

      case 5: // Opinion Reminder (shared component)
        return (
          <div className="space-y-5">
            <SaleOpinionSection
              sale={localSale}
              onSaleChange={handleChange}
              isDisabled={isDisabled}
            />
          </div>
        );

      case 6: // Contract PDF Generation
        return (
        <div className="rounded-xl border border-neutral-100 bg-neutral-50/70 p-4 md:p-6 space-y-6">
          {localSale?.attachmentUrl && (
            <div className="flex justify-end mb-2">
              <Tooltip content={t("sales.actions.view_contract_pdf") || "View Contract PDF"} position="bottom">
                <IconButton
                  actionName="View Contract PDF"
                  onClick={handleViewContractPDF}
                >
                  <FileText className="w-5 h-5 text-purple-600" />
                </IconButton>
              </Tooltip>
            </div>
          )}
          
          <div className="space-y-3">
            <Subtitle text={t("sales.form.contract.title") || "Contract PDF"} />
            <TinyText text={t("sales.form.contract.description") || 
              "Would you like to generate a printable PDF contract? It will also be attached to the sale. You can generate it later if needed."} />
            
            <div className="mt-2">
              <SmallCheckBox
                label={t("sales.form.contract.generate_pdf") || "Generate printable PDF contract"}
                defaultValue={wantToGenerateContract}
                onChange={(e) => setWantToGenerateContract(e.target.checked)}
                isDisabled={isDisabled || !localSale?.id}
              />
            </div>
          </div>
          
          {/* Contract fields - shown when user wants to generate contract */}
          {wantToGenerateContract && localSale?.id && (
            <>
              {/* Language Selection - Must be selected first */}
              <div className="space-y-3">
                <Subtitle text={t("sales.form.contract.select_language") || "Select Contract Language"} />
                <SelectInput
                  values={[
                    { value: 'EN_US', readableValue: t("sales.form.contract.language.en") || "English" },
                    { value: 'PL_PL', readableValue: t("sales.form.contract.language.pl") || "Polish" },
                    { value: 'ES_ES', readableValue: t("sales.form.contract.language.es") || "Spanish" },
                    { value: 'DE_DE', readableValue: t("sales.form.contract.language.de") || "German" },
                    { value: 'IT_IT', readableValue: t("sales.form.contract.language.it") || "Italian" },
                    { value: 'PT_PT', readableValue: t("sales.form.contract.language.pt") || "Portuguese" },
                    { value: 'UK_UA', readableValue: t("sales.form.contract.language.uk") || "Ukrainian" }
                  ]}
                  onChange={e => {
                    const lang = e.target.value;
                    setSelectedContractLanguage(lang);
                    // Reload contract template for the selected language
                    setContractTemplateLocale(lang);
                  }}
                  label={t("sales.form.contract.language") || "Contract Language"}
                  defaultValue={storeContractLocale}
                  isDisabled={isDisabled}
                />
              </div>

              {/* Only show fields if language is selected */}
              {selectedContractLanguage && (
                <>
                  {/* General Data - Always shown */}
                  <div className="space-y-3">
                    <Subtitle text={t("sales.form.contract.general_data") || t("contracts.general_data") || "General Data"} />
                    <TinyText text={t("sales.form.contract.general_data.helper") || "We need a bit more data to generate the contract"} />
                    <InputShortText
                      defaultValue={contractClientAddress}
                      label={`${t("sales.form.contract.resident") || t("contracts.resident") || "Resident"}*`}
                      onChange={e => setContractClientAddress(e.target.value)}
                      isDisabled={isDisabled}
                    />
                    <InputShortText
                      defaultValue={contractClientPersonalID}
                      label={t("sales.form.contract.personal_id") || t("contracts.personal_id") || "Personal ID"}
                      onChange={e => setContractClientPersonalID(e.target.value)}
                      isDisabled={isDisabled}
                    />
                  </div>

                  {/* Contract Conditions - Scrollable */}
                  {contractTemplate?.conditions?.conditions && contractTemplate.conditions.conditions.length > 0 && (
                    <div className="space-y-3">
                      <Subtitle text={t("sales.form.contract.conditions") || t("contracts.conditions") || "Conditions"} />
                      
                      {/* Contract Dates - Inside General Conditions */}
                      <div className="space-y-4 mb-2">
                        <CustomDatePicker
                          label={t("sales.form.contract.completion_date") || "The subject of the contract will be completed by:"}
                          value={completionDate}
                          onChange={(date) => {
                            if (date) {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const selectedDate = new Date(date);
                              selectedDate.setHours(0, 0, 0, 0);
                              if (selectedDate < today) {
                                toastService.showToast(
                                  t("sales.form.contract.completion_date.past_date_error") || "Completion date cannot be a past date",
                                  "error"
                                );
                                return;
                              }
                              setCompletionDate(date);
                            } else {
                              setCompletionDate(null);
                            }
                          }}
                          isDisabled={isDisabled}
                          timeZone={settings.timezone}
                        />
                      </div>
                      
                      <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
                        {contractTemplate.conditions.conditions.map((condition, index) => (
                          <p key={`condition-${index}`} className="text-sm">{`${index + 1}. ${condition}`}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Information - At the bottom */}
                  {(contractTemplate?.additionalRequirements?.requiresEmployeeSignature || contractTemplate?.additionalRequirements?.requiresFollowUpMeeting) && (
                    <div className="space-y-3">
                      <Subtitle text={t("sales.form.contract.additional_information") || t("contracts.additional_information") || "Additional Information"} />
                      {contractTemplate?.additionalRequirements?.requiresEmployeeSignature && (
                        <SelectInput
                          values={contractSigners.map(e => ({ value: e.name, readableValue: e.name }))}
                          onChange={e => setContractEmployeeName(e.target.value)}
                          label={t("sales.form.contract.signing_employee") || t("contracts.signing_employee") || "Signing Employee"}
                          defaultValue={contractEmployeeName || ''}
                          isDisabled={isDisabled}
                        />
                      )}
                      {contractTemplate?.additionalRequirements?.requiresFollowUpMeeting && (
                        <CustomDatePicker
                          label={t("contracts.first_measurement") || t("sales.form.contract.first_meeting_date") || "First Meeting Date"}
                          value={firstFollowUpMeetingDate}
                          onChange={(date) => {
                            setFirstFollowUpMeetingDate(date);
                          }}
                          isDisabled={isDisabled}
                          timeZone={settings.timezone}
                        />
                      )}
                    </div>
                  )}

                </>
              )}
            </>
          )}
        </div>
      );

      default:
        return null;
    }
  };

  const currentStepData = STEPS.find(step => step.id === currentStep);

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-500">
            {t("sales.form.step", { current: currentStep, total: STEPS.length }) || `Step ${currentStep} of ${STEPS.length}`}
          </span>
          <span className="text-sm text-gray-400">•</span>
          <span className="text-sm text-gray-600">
            {currentStepData ? (t(currentStepData.titleKey) || currentStepData.titleKey) : ''}
          </span>
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {renderStepContent()}
      </div>

      {/* Add Model Modal */}
      {showItemModal && newItem && (
        <Modal 
          modalTitle={t("sales.form.add_model") || "Add a new model"}
          canClose 
          onClose={() => {
            setShowItemModal(false);
            setNewItem(null);
          }}
        >
          <>
            <div className="space-y-5">
              <InputShortText 
                label={t("admin.collections.items.form.name") || "Name"} 
                validationFn={(value) => !!value && isValidText(value, 3)} 
                onChange={e => setNewItem({ ...newItem, name: e.currentTarget.value } as Item)}
                errorMessage={"errors.too_short"}
                defaultValue={newItem?.name || ''}
                isDisabled={isDisabled}
              />
              <PriceField 
                label={t("admin.collections.items.form.price") || "Price"}
                currency={newItem?.currency || storeDefaultCurrencyForNewItem}
                price={newItem?.price || 0}
                currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                onCurrencyChange={e => setNewItem({ ...newItem, currency: e } as Item)}
                onPriceChange={value => setNewItem({ ...newItem, price: value, currency: newItem?.currency || storeDefaultCurrencyForNewItem } as Item)}
                isDisabled={isDisabled}
              />
            </div>
            <div className="flex justify-center mt-5">
              <PrimaryButton 
                text={t("global.create") || "Create"} 
                fn={handleSaveItem}
                actionName="Create Item"
                isDisabled={!newItem?.name || !isValidText(newItem.name, 3) || !newItem.price || newItem.price <= 0}
              />
            </div>
          </>
        </Modal>
      )}

      {/* Collection Selection Modal */}
      {showCatalogueSelectionModal && newItem && collections.length > 0 && (
        <Modal 
          modalTitle={t("sales.form.select_collection") || "Select Collection"}
          canClose 
          onClose={() => {
            setShowCatalogueSelectionModal(false);
            setNewItem(null);
            setSelectedCollectionId(null);
          }}
        >
          <>
            <div className="space-y-5">
              <SelectInput
                values={collections.map(c => ({ value: String(c.id!), readableValue: c.name }))}
                onChange={(e) => setSelectedCollectionId(Number(e.target.value))}
                label={t("sales.form.select_collection") || "Select Collection"}
                defaultValue={selectedCollectionId ? String(selectedCollectionId) : ''}
                isDisabled={isDisabled}
              />
            </div>
            <div className="flex justify-center mt-5">
              <PrimaryButton 
                text={t("global.add") || "Add"} 
                fn={handleAddItemToCollection}
                actionName="Add Item to Collection"
                isDisabled={!selectedCollectionId}
              />
            </div>
          </>
        </Modal>
      )}

      {/* Add Employee Modal */}
      {showEmployeeModal && newEmployee && (
        <Modal 
          modalTitle={t("sales.form.create_employee") || "Create Employee"}
          canClose 
          onClose={() => {
            setShowEmployeeModal(false);
            setNewEmployee(null);
          }}
        >
          <>
            <div className="space-y-5">
              <InputShortText 
                label={t("admin.employee.form.name") || "Name"} 
                validationFn={(value) => !!value && isValidText(value, 3)} 
                onChange={e => setNewEmployee({ ...newEmployee, name: e.currentTarget.value } as Employee)}
                errorMessage={"errors.too_short"}
                defaultValue={newEmployee?.name || ''}
                isDisabled={isDisabled}
              />
              <Divider />
              <SmallCheckBox
                label={`${t("admin.employee.form.employed")}?`} 
                defaultValue={newEmployee?.active ?? true}
                onChange={e => {
                  setNewEmployee({ 
                    ...newEmployee, 
                    active: e.target.checked
                  } as Employee);
                }}
                isDisabled={isDisabled}
              />
              <SmallCheckBox
                label={`${t("admin.employee.form.meeting_removal") || "Meeting removal"}?`} 
                defaultValue={contains(newEmployee, 'meetingRemoval')}
                onChange={e => {
                  const currentFlags = newEmployee?.flags || [];
                  const updatedFlags = e.target.checked 
                    ? [...currentFlags.filter(f => f !== 'meetingRemoval'), 'meetingRemoval']
                    : currentFlags.filter(f => f !== 'meetingRemoval');
                  setNewEmployee({ 
                    ...newEmployee, 
                    flags: updatedFlags
                  } as Employee);
                }}
                isDisabled={isDisabled}
              />
              <SmallCheckBox
                label={`${t("admin.employee.form.sign_contracts") || "Sign contracts"}?`} 
                defaultValue={true}
                onChange={e => {
                  const currentFlags = newEmployee?.flags || [];
                  const updatedFlags = e.target.checked 
                    ? [...currentFlags.filter(f => f !== 'contractSignature'), 'contractSignature']
                    : currentFlags.filter(f => f !== 'contractSignature');
                  setNewEmployee({ 
                    ...newEmployee, 
                    flags: updatedFlags
                  } as Employee);
                }}
                isDisabled={true} // Required, cannot be unchecked
              />
              <SmallCheckBox
                label={`${t("admin.employee.form.sales_representative") || "Sales representative"}?`}
                defaultValue={contains(newEmployee, 'salesRepresentative')}
                onChange={e => {
                  const currentFlags = newEmployee?.flags || [];
                  const updatedFlags = e.target.checked 
                    ? [...currentFlags.filter(f => f !== 'salesRepresentative'), 'salesRepresentative']
                    : currentFlags.filter(f => f !== 'salesRepresentative');
                  setNewEmployee({ 
                    ...newEmployee, 
                    flags: updatedFlags
                  } as Employee);
                }}
                isDisabled={isDisabled}
              />
            </div>
            <div className="flex justify-center mt-5">
              <PrimaryButton 
                text={t("global.create") || "Create"} 
                fn={handleSaveEmployee}
                actionName="Create Employee"
                isDisabled={!newEmployee?.name || !isValidText(newEmployee.name, 3)}
              />
            </div>
          </>
        </Modal>
      )}

    </div>
  );
}

export default SaleFormSteps;

